import { type Result, ok, err } from 'neverthrow';
import {
  createUnregisteredItem,
  createUnregisteredMap,
  EntryType,
  type TreeNode,
} from '@morph-mapper/types';
import {
  PluginFrameworkError,
  PluginFrameworkErrorType,
  TreeApi,
} from '../types';
import { nanoid } from 'nanoid';
import { constructPathToIdMapping } from '../utils';

/**
 * Creates an API over the supplied entries, the exposed functions are safety-checked to prevent
 * plugins from corrupting the state.
 *
 * @param entries The entries to create the API over.
 */
export const createTreeApi = (entries: Record<string, TreeNode>): TreeApi => {
  let mapping = constructPathToIdMapping(entries);

  /**
   * Internal safety function, fetches the ID of a node from a path.
   * @param path The path to the node.
   * @returns Ok(id) or PATH_NOT_FOUND.
   */
  const _getId = (path: string[]): Result<string, PluginFrameworkError> => {
    const id = mapping.get(JSON.stringify(path));
    if (id === undefined) {
      return err({
        type: PluginFrameworkErrorType.PATH_NOT_FOUND,
        message: `Path ${JSON.stringify(path)} does not exist in the tree.`,
      });
    }
    return ok(id);
  };

  /**
   * Internal safety function, fetches a node from an ID.
   * @param id The ID of the node.
   * @returns Ok(node) or CORRUPTED_STATE.
   */
  const _getEntry = (id: string): Result<TreeNode, PluginFrameworkError> => {
    if (entries[id] === undefined) {
      return err({
        type: PluginFrameworkErrorType.CORRUPTED_STATE,
        message: `Entry with id ${id} does not exist in the tree.`,
      });
    }

    return ok(entries[id]);
  };

  const getPaths = (type?: EntryType) => {
    const paths = Array.from(mapping.keys()).map(
      (k) => JSON.parse(k) as string[]
    );

    if (type) {
      let newPaths: string[][] = [];
      for (const path of paths) {
        const value = _getId(path).andThen((id) => _getEntry(id));
        if (value.isErr()) continue;
        if (value.value.type === type) newPaths.push(path);
      }
      return ok(newPaths);
    } else {
      return ok(paths);
    }
  };

  const getValue = (path: string[]) => {
    if (path.length === 0) {
      return err({
        type: PluginFrameworkErrorType.INVALID_PATH,
        message: 'Cannot get entry from empty path.',
      });
    }
    return _getId(path).andThen((id) => _getEntry(id));
  };

  const addKey = (path: string[], type: EntryType, node: any) => {
    if (path.length < 2) {
      return err({
        type: PluginFrameworkErrorType.INVALID_PATH,
        message: 'Cannot add at the root path',
      });
    }
    if (path[path.length - 1] !== node.key) {
      return err({
        type: PluginFrameworkErrorType.INVALID_ARGUMENTS,
        message: `The last element of the path must match the key of the node. Expected ${
          node.key
        }, got ${path[path.length - 1]}.`,
      });
    }

    // Verify the path (without the new value) is valid
    const partialPath: string[] = [];
    for (let i = 0; i < path.length - 1; i++) {
      partialPath.push(path[i]);
      if (mapping.get(JSON.stringify(partialPath)) === undefined) {
        return err({
          type: PluginFrameworkErrorType.INVALID_PATH,
          message: `Invalid path at index ${i}: ${partialPath}`,
        });
      }
    }

    // Verify the key is not already in use
    if (mapping.has(JSON.stringify(path))) {
      return err({
        type: PluginFrameworkErrorType.KEY_ALREADY_EXISTS,
        message: `Key ${path.join('.')} already exists in the tree.`,
      });
    }

    const parentPath = path.slice(0, -1);

    return _getId(parentPath)
      .andThen((parentId) => {
        const parent = entries[parentId];
        return parent.type === EntryType.Map
          ? ok({ parentId, parent })
          : err({
              type: PluginFrameworkErrorType.INVALID_PATH,
              message: `Cannot add a key to a non-map type at path ${parentPath.join(
                '.'
              )}.`,
            });
      })
      .andThen(({ parentId, parent }) => {
        let id = nanoid(10);
        while (entries[id]) {
          id = nanoid(10);
        }

        parent.children.forwardMap[id] = node.key;
        parent.children.reverseMap[node.key] = id;
        if (type === EntryType.Map) {
          entries[id] = {
            id,
            ...createUnregisteredMap({
              ...node,
              name: node.name ?? 'test',
              parentId,
              outputType: 'group',
              formerOutputType: 'group',
            }),
          };
        } else {
          let parsedValue;
          if (type === EntryType.Code && node.value !== undefined) {
            try {
              parsedValue = JSON.parse(node.value);
            } catch (e) {
              return err({
                type: PluginFrameworkErrorType.INVALID_ARGUMENTS,
                message: `Failed to parse code value as JSON`,
              });
            }
          }
          entries[id] = {
            id,
            ...createUnregisteredItem(type, {
              ...node,
              parentId,
              value: parsedValue ?? node.value,
            }),
          };
        }

        mapping = constructPathToIdMapping(entries);
        return ok();
      })
      .mapErr((e) => {
        console.error(
          'addKey failed:',
          PluginFrameworkErrorType[e.type],
          e.message
        );
        return e;
      });
  };

  const removeKey = (path: string[]) => {
    if (path.length === 0) {
      return err({
        type: PluginFrameworkErrorType.INVALID_PATH,
        message: `Cannot remove entry with path ${path.join('.')}`,
      });
    }

    return _getId(path)
      .andThen((id) => _getEntry(id))
      .andThen((node) => {
        if (node.dependencies.requiredBy.size > 0) {
          return err({
            type: PluginFrameworkErrorType.KEY_IN_USE,
            message: `Cannot remove entry with path ${path.join(
              '.'
            )}, it is required by other entries.`,
          });
        }
        return ok(node);
      })
      .andThen((node) => {
        if (node.parentId === undefined) {
          return err({
            type: PluginFrameworkErrorType.CORRUPTED_STATE,
            message: `Cannot remove entry with path ${path.join(
              '.'
            )}, it has no parent.`,
          });
        }
        const parent = entries[node.parentId];
        return parent.type === EntryType.Map
          ? ok({ parent, node })
          : err({
              type: PluginFrameworkErrorType.CORRUPTED_STATE,
              message: `Cannot remove entry with path ${path.join(
                '.'
              )}, it is not a map type.`,
            });
      })
      .andThen(({ parent, node }) => {
        delete parent.children.forwardMap[node.id];
        delete parent.children.reverseMap[node.key];
        return ok(node.id);
      })
      .andThen((id) => {
        delete entries[id];
        mapping = constructPathToIdMapping(entries);
        return ok();
      });
  };

  const getEnums = (values: string[]) => {
    const allPathsResult = getPaths();
    if (allPathsResult.isErr()) return err(allPathsResult.error);

    const allPaths = allPathsResult.value;

    const filtered = allPaths.filter((path) => {
      const fieldKey = path[path.length - 1];
      return values.includes(fieldKey);
    });

    const enums: Record<string, string[]> = {};

    for (const path of filtered) {
      const valueResult = getValue(path);
      if (valueResult.isOk()) {
        const node = valueResult.value;
        if (node.validation?._def?.typeName === 'ZodEnum') {
          const key = path[path.length - 1];
          enums[key] = node.validation._def.values;
        }
      }
    }

    return ok(enums);
  };

  return {
    getPaths,
    getValue,
    addKey,
    removeKey,
    getEnums,
  };
};
